import "./NewCircumferenceRecord.css";
import "./SkinfoldRecord.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import CheckedIcon from "../../assets/imgs/checked-icon.svg";
import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import TextInput from "../../components/TextInput/TextInput";
import loginService from "../../services/login-service";
import measurementService from "../../services/measurement-service";
import userService from "../../services/user-service";
import { GlobalContext } from "../GlobalContext";
import { buildAnthropometryHubPath } from "./draft-session-utils";
import {
  clearPendingSection,
  readPendingEdit,
  writePendingSection,
} from "./edit-pending-utils";
import { useAutoSave } from "./useAutoSave";
import {
  formatVisitDateLabel,
  toApiDate,
  todayDisplayDate,
} from "./circumference-utils";
import {
  SKINFOLD_PROTOCOLS,
  SKINFOLD_SITES,
  buildSkinfoldSavePayloadV2,
  computeSiteMean,
  createEmptySkinfoldState,
  formatMm,
  getFilledSiteKeys,
  getMissingSitesForProtocol,
  hasAnyMeasure,
  hasUnusualMeasure,
  isProtocolCompatible,
  isSiteFilled,
  protocolsFromMeasurement,
  sitesStateFromMeasurement,
  skinfoldEditMeasurementCacheKey,
} from "./skinfold-utils";
import {
  buildBodyFatResults,
  buildCalculationContext,
  calculateBodyComposition,
  calculateProtocolBodyFat,
  formatKg,
  formatPercent,
  resolveCompositionProtocolId,
} from "./skinfold-bodyfat";
const SkinfoldRecord = () => {
  const { id: patientId } = useParams();
  const [searchParams] = useSearchParams();
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const modeParam = searchParams.get("mode");
  const measurementIdParam = searchParams.get("measurementId");
  const isEditMode = modeParam === "edit";
  const isDraftSession = modeParam === "new";
  const isUpdatingExisting = Boolean(measurementIdParam);

  const [patient, setPatient] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [previousMeasurement, setPreviousMeasurement] = useState(null);
  const [sourceMeasurementId, setSourceMeasurementId] = useState(null);
  const [visitDate, setVisitDate] = useState(todayDisplayDate());
  const [sitesState, setSitesState] = useState(() => createEmptySkinfoldState());
  const [selectedSiteKey, setSelectedSiteKey] = useState(SKINFOLD_SITES[0].key);
  const [selectedProtocols, setSelectedProtocols] = useState([]);
  const [compositionProtocolId, setCompositionProtocolId] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [showUnusualModal, setShowUnusualModal] = useState(false);
  const [pendingMeasureUpdate, setPendingMeasureUpdate] = useState(null);
  const [formError, setFormError] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const skipAutoSaveRef = useRef(true);
  const baselineVisitDateRef = useRef(null);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        visitDate,
        sitesState,
        selectedProtocols,
        compositionProtocolId,
      }),
    [visitDate, sitesState, selectedProtocols, compositionProtocolId]
  );

  const isDirty = currentSnapshot !== initialSnapshot;
  const filledKeys = useMemo(() => getFilledSiteKeys(sitesState), [sitesState]);
  const hasMeasures = useMemo(() => hasAnyMeasure(sitesState), [sitesState]);

  const calculationContext = useMemo(
    () =>
      buildCalculationContext({
        patient,
        previousMeasurement,
        visitDate,
      }),
    [patient, previousMeasurement, visitDate]
  );

  const visibleProtocols = SKINFOLD_PROTOCOLS;

  const bodyFatResults = useMemo(
    () =>
      buildBodyFatResults({
        sitesState,
        selectedProtocols,
        patient,
        context: calculationContext,
        filledKeys,
      }),
    [sitesState, selectedProtocols, patient, calculationContext, filledKeys]
  );

  useEffect(() => {
    setCompositionProtocolId((current) => {
      const validRows = bodyFatResults.rows.filter((row) => row.percent != null);
      if (!validRows.length) {
        return null;
      }
      if (current && validRows.some((row) => row.id === current)) {
        return current;
      }
      return validRows[0]?.id ?? null;
    });
  }, [selectedProtocols, bodyFatResults.rows]);

  const compositionReference = useMemo(() => {
    if (!bodyFatResults.rows.length) {
      return null;
    }
    return (
      bodyFatResults.rows.find(
        (row) => row.id === compositionProtocolId && row.percent != null
      ) ||
      bodyFatResults.rows.find((row) => row.percent != null) ||
      null
    );
  }, [bodyFatResults.rows, compositionProtocolId]);

  const composition = useMemo(
    () =>
      calculateBodyComposition(
        calculationContext.weightKg,
        compositionReference?.percent ?? null
      ),
    [calculationContext.weightKg, compositionReference]
  );

  const selectedSite = useMemo(
    () => sitesState.find((site) => site.key === selectedSiteKey) || sitesState[0],
    [sitesState, selectedSiteKey]
  );

  const selectedSiteMeta = useMemo(
    () => SKINFOLD_SITES.find((site) => site.key === selectedSiteKey) || SKINFOLD_SITES[0],
    [selectedSiteKey]
  );

  const canSave = isDirty && filledKeys.length > 0 && visitDate?.length === 10;

  const pageTitle = isEditMode
    ? "Editar dobras cutâneas"
    : "Novo registro de dobras";

  const breadcrumbMeasurementId = sourceMeasurementId || measurementIdParam;
  const anthropometryHref =
    isDraftSession && breadcrumbMeasurementId
      ? buildAnthropometryHubPath(patientId, {
          mode: "new",
          measurementId: breadcrumbMeasurementId,
        })
      : breadcrumbMeasurementId
        ? `/patients/${patientId}/anthropometry?measurementId=${breadcrumbMeasurementId}`
        : `/patients/${patientId}/anthropometry`;

  const buildCurrentPayload = useCallback(() => {
    const protocolsForSave =
      compositionProtocolId && selectedProtocols.includes(compositionProtocolId)
        ? [
            compositionProtocolId,
            ...selectedProtocols.filter((id) => id !== compositionProtocolId),
          ]
        : selectedProtocols;

    return buildSkinfoldSavePayloadV2({
      nutritionistId: currentUser?.id,
      patientId,
      visitDate: toApiDate(visitDate),
      sitesState,
      selectedProtocols: protocolsForSave,
      patient,
      previousMeasurement,
      measurementId: sourceMeasurementId || measurementIdParam,
    });
  }, [
    compositionProtocolId,
    selectedProtocols,
    currentUser?.id,
    patientId,
    visitDate,
    sitesState,
    patient,
    previousMeasurement,
    sourceMeasurementId,
    measurementIdParam,
  ]);

  const buildPendingSectionData = useCallback(
    () => ({
      payload: buildCurrentPayload(),
      formState: {
        visitDate,
        sitesState,
        selectedProtocols,
        compositionProtocolId,
      },
    }),
    [
      buildCurrentPayload,
      visitDate,
      sitesState,
      selectedProtocols,
      compositionProtocolId,
    ]
  );

  const persistPending = useCallback(
    async (data) => {
      writePendingSection(patientId, measurementIdParam, "skinfold", data);
    },
    [patientId, measurementIdParam]
  );

  const { saveStateLabel, scheduleSave, cancelPending, flush } = useAutoSave({
    persist: persistPending,
  });

  useEffect(() => {
    skipAutoSaveRef.current = true;
  }, [measurementIdParam, patientId]);

  useEffect(() => {
    if (isDraftSession && !measurementIdParam) {
      window.location.href = `/patients/${patientId}/anthropometry`;
    }
  }, [isDraftSession, measurementIdParam, patientId]);

  const fetchData = useCallback(async () => {
    skipAutoSaveRef.current = true;
    setIsLoading(true);
    try {
      const [userFound, patientFound, histories] = await Promise.all([
        loginService.getCurrentUser(),
        userService.getUserById(patientId),
        measurementService.listByPatient(patientId),
      ]);

      setCurrentUser(userFound);
      setPatient(patientFound);

      let cachedMeasurement = null;
      if (isEditMode && measurementIdParam) {
        try {
          const cachedRaw = sessionStorage.getItem(
            skinfoldEditMeasurementCacheKey(patientId, measurementIdParam)
          );
          cachedMeasurement = cachedRaw ? JSON.parse(cachedRaw) : null;
        } catch {
          cachedMeasurement = null;
        }
      }

      const matchedMeasurement = isUpdatingExisting
        ? histories?.find((item) => String(item.id) === String(measurementIdParam))
        : null;

      const sourceMeasurement =
        matchedMeasurement ||
        (cachedMeasurement &&
        String(cachedMeasurement.id) === String(measurementIdParam)
          ? cachedMeasurement
          : null);

      if (
        matchedMeasurement &&
        cachedMeasurement &&
        String(cachedMeasurement.id) === String(measurementIdParam)
      ) {
        sessionStorage.removeItem(
          skinfoldEditMeasurementCacheKey(patientId, measurementIdParam)
        );
      }

      const latestWithMetabolic = (histories || []).find(
        (item) => item?.metabolic_data?.weight_kg != null
      );

      let contextMeasurement = isUpdatingExisting
        ? sourceMeasurement || histories?.find((item) => String(item.id) === String(measurementIdParam)) || null
        : isEditMode
          ? sourceMeasurement || histories?.[0] || null
          : null;

      if (
        contextMeasurement &&
        contextMeasurement.metabolic_data?.weight_kg == null &&
        latestWithMetabolic
      ) {
        contextMeasurement = {
          ...contextMeasurement,
          metabolic_data:
            contextMeasurement.metabolic_data || latestWithMetabolic.metabolic_data,
          circumference:
            contextMeasurement.circumference || latestWithMetabolic.circumference,
        };
      }

      setPreviousMeasurement(contextMeasurement);
      setSourceMeasurementId(sourceMeasurement?.id || (isUpdatingExisting ? Number(measurementIdParam) : null));

      let nextVisitDate = todayDisplayDate();
      let nextSites = createEmptySkinfoldState();
      let nextProtocols = [];
      let nextCompositionProtocolId = null;

      if (sourceMeasurement && (isEditMode || isUpdatingExisting)) {
        baselineVisitDateRef.current = sourceMeasurement.visit_date || null;
        nextVisitDate = formatVisitDateLabel(sourceMeasurement.visit_date) || nextVisitDate;
        nextSites = sitesStateFromMeasurement(sourceMeasurement);
        nextProtocols = protocolsFromMeasurement(sourceMeasurement);
      } else if (isUpdatingExisting && measurementIdParam) {
        setFormError("Registro não encontrado. Volte ao histórico e tente novamente.");
      } else if (isEditMode && measurementIdParam) {
        setFormError("Registro não encontrado. Volte ao histórico e tente novamente.");
      }

      const pending = isUpdatingExisting
        ? readPendingEdit(patientId, measurementIdParam)
        : null;
      const pendingForm = pending?.skinfold?.formState;
      if (pendingForm) {
        nextVisitDate = pendingForm.visitDate ?? nextVisitDate;
        nextSites = pendingForm.sitesState ?? nextSites;
        nextProtocols = pendingForm.selectedProtocols ?? nextProtocols;
        nextCompositionProtocolId = pendingForm.compositionProtocolId ?? nextCompositionProtocolId;
      }

      const firstFilled = nextSites.find((site) => isSiteFilled(site));
      const loadContext = buildCalculationContext({
        patient: patientFound,
        previousMeasurement: contextMeasurement,
        visitDate: nextVisitDate,
      });

      if (nextCompositionProtocolId == null) {
        nextCompositionProtocolId = resolveCompositionProtocolId({
          measurement: sourceMeasurement,
          sitesState: nextSites,
          selectedProtocols: nextProtocols,
          patient: patientFound,
          context: loadContext,
          filledKeys: getFilledSiteKeys(nextSites),
        });
      }

      setVisitDate(nextVisitDate);
      setSitesState(nextSites);
      setSelectedProtocols(nextProtocols);
      setCompositionProtocolId(nextCompositionProtocolId);
      setSelectedSiteKey(firstFilled?.key || SKINFOLD_SITES[0].key);
      setInitialSnapshot(
        JSON.stringify({
          visitDate: nextVisitDate,
          sitesState: nextSites,
          selectedProtocols: nextProtocols,
          compositionProtocolId: nextCompositionProtocolId,
        })
      );
    } catch (error) {
      console.error(error);
      const nextVisitDate = todayDisplayDate();
      const nextSites = createEmptySkinfoldState();
      setVisitDate(nextVisitDate);
      setSitesState(nextSites);
      setSelectedProtocols([]);
      setCompositionProtocolId(null);
      setInitialSnapshot(
        JSON.stringify({
          visitDate: nextVisitDate,
          sitesState: nextSites,
          selectedProtocols: [],
          compositionProtocolId: null,
        })
      );
      setFormError("Não foi possível carregar o histórico. Você pode registrar as dobras.");
    } finally {
      setIsLoading(false);
      skipAutoSaveRef.current = false;
    }
  }, [
    patientId,
    setIsLoading,
    isEditMode,
    isUpdatingExisting,
    measurementIdParam,
  ]);

  useEffect(() => {
    document.title = pageTitle;
    setSelectedScreen("patients");
    fetchData();
  }, [fetchData, setSelectedScreen, pageTitle]);

  useEffect(() => {
    const canAutoSave =
      (isDraftSession || isEditMode) && measurementIdParam && currentUser && isDirty;
    if (!canAutoSave) {
      return;
    }
    if (skipAutoSaveRef.current) {
      return;
    }
    if (filledKeys.length === 0) {
      clearPendingSection(patientId, measurementIdParam, "skinfold");
      return;
    }
    scheduleSave(buildPendingSectionData());
  }, [
    buildPendingSectionData,
    compositionProtocolId,
    currentUser,
    filledKeys.length,
    isDirty,
    isDraftSession,
    isEditMode,
    measurementIdParam,
    patientId,
    scheduleSave,
    selectedProtocols,
    sitesState,
    visitDate,
  ]);

  const applyMeasureUpdate = (siteKey, measureIndex, value) => {
    setSitesState((current) =>
      current.map((site) => {
        if (site.key !== siteKey) {
          return site;
        }
        const measures = [...site.measures];
        measures[measureIndex] = value;
        return {
          ...site,
          measures,
          mean: computeSiteMean(measures),
        };
      })
    );
    setFormError("");
  };

  const handleMeasureChange = (measureIndex, value) => {
    const nextMeasures = [...(selectedSite?.measures || ["", "", ""])];
    nextMeasures[measureIndex] = value;

    if (hasUnusualMeasure(nextMeasures) && !hasUnusualMeasure(selectedSite?.measures)) {
      setPendingMeasureUpdate({
        siteKey: selectedSiteKey,
        measureIndex,
        value,
      });
      setShowUnusualModal(true);
      return;
    }

    applyMeasureUpdate(selectedSiteKey, measureIndex, value);
  };

  const handleConfirmUnusual = () => {
    if (pendingMeasureUpdate) {
      applyMeasureUpdate(
        pendingMeasureUpdate.siteKey,
        pendingMeasureUpdate.measureIndex,
        pendingMeasureUpdate.value
      );
    }
    setPendingMeasureUpdate(null);
    setShowUnusualModal(false);
  };

  const handleCancelUnusual = () => {
    setPendingMeasureUpdate(null);
    setShowUnusualModal(false);
  };

  const handleToggleProtocol = (protocolId) => {
    const protocol = SKINFOLD_PROTOCOLS.find((item) => item.id === protocolId);
    if (!protocol || !isProtocolCompatible(protocol, filledKeys, patient)) {
      return;
    }

    setSelectedProtocols((current) =>
      current.includes(protocolId)
        ? current.filter((id) => id !== protocolId)
        : [...current, protocolId]
    );
  };

  useEffect(() => {
    setSelectedProtocols((current) =>
      current.filter((protocolId) => {
        const protocol = SKINFOLD_PROTOCOLS.find((item) => item.id === protocolId);
        return protocol && isProtocolCompatible(protocol, filledKeys, patient);
      })
    );
  }, [filledKeys, patient]);

  const handleSelectCompositionProtocol = (protocolId) => {
    const row = bodyFatResults.rows.find((item) => item.id === protocolId);
    if (!row || row.percent == null) {
      return;
    }
    setCompositionProtocolId(protocolId);
  };

  const validateBeforeSave = () => {
    if (!canSave) {
      setFormError("Preencha ao menos uma dobra completa para salvar.");
      return false;
    }

    if (!toApiDate(visitDate)) {
      setFormError("Informe uma data de medição válida.");
      return false;
    }

    return true;
  };

  const handleReturnToDraftHub = () => {
    window.location.href = buildAnthropometryHubPath(patientId, {
      mode: "new",
      measurementId: measurementIdParam,
    });
  };

  const handleDiscardChanges = async () => {
    if (!isDirty) {
      handleReturnToDraftHub();
      return;
    }

    if (!initialSnapshot) {
      return;
    }

    cancelPending();

    const parsed = JSON.parse(initialSnapshot);
    const apiVisitDate =
      toApiDate(parsed.visitDate) || toApiDate(baselineVisitDateRef.current);
    const hasBaselineData = getFilledSiteKeys(parsed.sitesState || []).length > 0;

    if (hasBaselineData && apiVisitDate) {
      writePendingSection(patientId, measurementIdParam, "skinfold", {
        payload: buildSkinfoldSavePayloadV2({
          nutritionistId: currentUser?.id,
          patientId,
          visitDate: apiVisitDate,
          sitesState: parsed.sitesState,
          selectedProtocols: parsed.selectedProtocols,
          patient,
          previousMeasurement,
          measurementId: sourceMeasurementId || measurementIdParam,
        }),
        formState: {
          visitDate: parsed.visitDate,
          sitesState: parsed.sitesState,
          selectedProtocols: parsed.selectedProtocols,
          compositionProtocolId: parsed.compositionProtocolId,
        },
      });
    } else {
      clearPendingSection(patientId, measurementIdParam, "skinfold");
    }

    handleReturnToDraftHub();
  };

  const handleReturnToAnthropometry = () => {
    window.location.href = measurementIdParam
      ? `/patients/${patientId}/anthropometry?measurementId=${measurementIdParam}`
      : `/patients/${patientId}/anthropometry`;
  };

  const handleSaveAndReturn = async () => {
    if (!isDirty) {
      if (isDraftSession) {
        handleReturnToDraftHub();
      } else {
        handleReturnToAnthropometry();
      }
      return;
    }

    if (!canSave) {
      setFormError("Preencha ao menos uma dobra completa para continuar.");
      return;
    }

    if (filledKeys.length > 0) {
      await persistPending(buildPendingSectionData());
    }
    await flush();

    if (isDraftSession) {
      handleReturnToDraftHub();
    } else {
      handleReturnToAnthropometry();
    }
  };

  return (
    <div className="new-circumference-record skinfold-record">
      <div className="new-circumference-record__top">
        <div className="new-circumference-record__breadcrumb">
        <a href="/patients" className="new-circumference-record__breadcrumb__link">
          Pacientes
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={`/patients/${patientId}`}
          className="new-circumference-record__breadcrumb__link"
        >
          {patient?.name || "Paciente"}
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={anthropometryHref}
          className="new-circumference-record__breadcrumb__link"
        >
          Antropometria
        </a>
        <div className="new-circumference-record__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <p
          className={`new-circumference-record__breadcrumb__current ${
            isDraftSession ? "new-circumference-record__breadcrumb__current--draft" : ""
          }`}
        >
          {isEditMode ? "Editar" : isDraftSession ? "Rascunho" : "Novo registro"}
        </p>
        </div>
      </div>

      <div className="new-circumference-record__shell">
        <div className="new-circumference-record__hero">
          <div className="new-circumference-record__hero__copy">
            <p className="new-circumference-record__hero__eyebrow">Antropometria</p>
            <h1>{pageTitle}</h1>
            <p>
              {isEditMode
                ? "Atualize as dobras deste registro. As alterações são salvas automaticamente nesta tela e só entram no histórico ao salvar no hub."
                : isDraftSession
                  ? "Meça no hemicorpo direito, em triplicata. As alterações são salvas automaticamente nesta tela e só entram no histórico ao salvar no hub."
                  : "Meça no hemicorpo direito, em triplicata. A média de cada sítio alimenta os protocolos compatíveis."}
            </p>
          </div>
          <div className="new-circumference-record__date">
            <p className="new-circumference-record__date__label">Data da medição</p>
            <p className="new-circumference-record__date__value">{visitDate}</p>
            {(isDraftSession || isEditMode) && saveStateLabel && (
              <p className="new-circumference-record__autosave">{saveStateLabel}</p>
            )}
          </div>
        </div>

        <div className="skinfold-record__workspace">
          <div className="skinfold-record__row">
            <section className="skinfold-record__card skinfold-record__measures">
              <div className="skinfold-record__card-header">
                <h2>Medidas</h2>
                <p className="skinfold-record__card-subtitle">
                  Selecione um sítio para preencher as três medidas
                </p>
              </div>
              <div className="skinfold-record__measures-body">
                <aside className="skinfold-record__sites">
                  <ul className="skinfold-record__sites-list">
                    {SKINFOLD_SITES.map((site) => {
                      const state = sitesState.find((item) => item.key === site.key);
                      const filled = isSiteFilled(state);
                      const active = site.key === selectedSiteKey;

                      return (
                        <li key={site.key}>
                          <button
                            type="button"
                            className={`skinfold-record__site-button${
                              active ? " skinfold-record__site-button--active" : ""
                            }${filled ? " skinfold-record__site-button--filled" : ""}`}
                            onClick={() => setSelectedSiteKey(site.key)}
                          >
                            <span className="skinfold-record__site-label">{site.label}</span>
                            <span className="skinfold-record__site-mean">
                              {filled ? formatMm(state.mean) : "—"}
                            </span>
                            <span className="skinfold-record__site-chevron" aria-hidden="true">
                              <img src={ChevronRightIcon} alt="" />
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>

                <div className="skinfold-record__editor">
                  <h3>{selectedSiteMeta.label}</h3>
                  <p className="skinfold-record__editor-hint">
                    Três medidas em mm · média automática
                  </p>

                  <div className="skinfold-record__measures-row">
                    {[0, 1, 2].map((index) => (
                      <div key={index} className="skinfold-record__measure-field">
                        <label className="skinfold-record__measure-label">
                          Medida {index + 1}
                        </label>
                        <TextInput
                          placeholder="0"
                          value={selectedSite?.measures?.[index] || ""}
                          onChangeText={(value) => handleMeasureChange(index, value)}
                          type="decimal"
                          size="small"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="skinfold-record__mean-box">
                    <p className="skinfold-record__mean-label">Média</p>
                    <p className="skinfold-record__mean-value">
                      {selectedSite?.mean != null ? formatMm(selectedSite.mean) : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <aside className="skinfold-record__card skinfold-record__context-panel">
              <div className="skinfold-record__card-header">
                <h2>Perfil antropométrico</h2>
                <p className="skinfold-record__card-subtitle">
                  Sexo, idade, peso e altura usados nos cálculos
                </p>
              </div>
              <div className="skinfold-record__context-body">
                <ul className="skinfold-record__context-list">
                  <li className="skinfold-record__context-row">
                    <span>Sexo</span>
                    <strong>{calculationContext.sexLabel}</strong>
                  </li>
                  <li className="skinfold-record__context-row">
                    <span>Idade</span>
                    <strong>
                      {calculationContext.age != null
                        ? `${calculationContext.age} anos`
                        : "—"}
                    </strong>
                  </li>
                  <li className="skinfold-record__context-row">
                    <span>Peso</span>
                    <strong>{formatKg(calculationContext.weightKg)}</strong>
                  </li>
                  <li className="skinfold-record__context-row">
                    <span>Altura</span>
                    <strong>
                      {calculationContext.heightCm != null
                        ? `${String(calculationContext.heightCm).replace(".", ",")} cm`
                        : "—"}
                    </strong>
                  </li>
                </ul>
                {calculationContext.missing.length > 0 && (
                  <p className="skinfold-record__context-warning">
                    Faltam {calculationContext.missing.join(", ")} no cadastro ou no
                    registro anterior. O % de gordura só calcula com sexo e idade;
                    a composição corporal também precisa do peso.
                  </p>
                )}
              </div>
            </aside>
          </div>

          {hasMeasures && (
            <section className="skinfold-record__card skinfold-record__protocols-panel">
              <div className="skinfold-record__card-header">
                <h2>Protocolos compatíveis</h2>
                <p className="skinfold-record__card-subtitle">
                  Marque os protocolos para incluí-los no registro e ver os resultados
                </p>
              </div>
              <div className="skinfold-record__protocols-body">
                <aside className="skinfold-record__protocol-nav">
                  {visibleProtocols.length > 0 ? (
                    <ul className="skinfold-record__protocols-list">
                      {visibleProtocols.map((protocol) => {
                        const missing = getMissingSitesForProtocol(
                          protocol,
                          filledKeys,
                          patient
                        );
                        const compatible = missing.length === 0;
                        const checked = selectedProtocols.includes(protocol.id);
                        const { percent } = calculateProtocolBodyFat({
                          protocolId: protocol.id,
                          sitesState,
                          patient,
                          age: calculationContext.age,
                          male: calculationContext.male,
                        });

                        return (
                          <li key={protocol.id}>
                            <button
                              type="button"
                              className={`skinfold-record__protocol-row${
                                checked ? " skinfold-record__protocol-row--checked" : ""
                              }${
                                !compatible
                                  ? " skinfold-record__protocol-row--disabled"
                                  : ""
                              }`}
                              disabled={!compatible}
                              onClick={() => handleToggleProtocol(protocol.id)}
                            >
                              <div
                                className={`skinfold-record__checkbox__box${
                                  checked ? " checkbox--checked" : ""
                                }`}
                              >
                                <img src={CheckedIcon} alt="" />
                              </div>
                              <span className="skinfold-record__protocol-row-label">
                                {protocol.label}
                                {!compatible && (
                                  <em className="skinfold-record__protocol-item-missing">
                                    Falta {missing.join(", ")}
                                  </em>
                                )}
                              </span>
                              <span className="skinfold-record__protocol-row-value">
                                {compatible ? formatPercent(percent) : "—"}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="skinfold-record__protocols-empty">
                      Nenhum protocolo foi marcado neste registro.
                    </p>
                  )}
                </aside>

                <div className="skinfold-record__editor skinfold-record__results-panel">
                  <h3>Percentual de gordura corporal</h3>
                  {bodyFatResults.rows.some((row) => row.percent != null) && (
                    <p className="skinfold-record__editor-hint">
                      Selecione qual protocolo usar na composição corporal
                    </p>
                  )}
                  {bodyFatResults.rows.length > 0 ? (
                    <ul className="skinfold-record__sites-list skinfold-record__bodyfat-list">
                      {bodyFatResults.rows.map((row) => {
                        const isReference = compositionReference?.id === row.id;
                        const isSelectable = row.percent != null;

                        return (
                          <li key={row.id}>
                            <button
                              type="button"
                              className={`skinfold-record__site-button${
                                isReference ? " skinfold-record__site-button--active" : ""
                              }${
                                isReference && row.percent != null
                                  ? " skinfold-record__site-button--filled"
                                  : ""
                              }${
                                row.percent == null
                                  ? " skinfold-record__site-button--muted"
                                  : ""
                              }`}
                              disabled={!isSelectable}
                              aria-pressed={isReference}
                              aria-label={`Usar ${row.label} na composição corporal`}
                              onClick={() => handleSelectCompositionProtocol(row.id)}
                            >
                              <span className="skinfold-record__site-label">
                                {row.label}
                                {row.note && (
                                  <em className="skinfold-record__protocol-item-missing">
                                    {row.note}
                                  </em>
                                )}
                              </span>
                              <span className="skinfold-record__site-mean">
                                {formatPercent(row.percent)}
                              </span>
                              <span className="skinfold-record__site-chevron" aria-hidden="true">
                                <img src={ChevronRightIcon} alt="" />
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="skinfold-record__bodyfat-empty">
                      {!calculationContext.canCalculatePercent
                        ? "Informe sexo e idade do paciente para calcular o percentual."
                        : "Marque um ou mais protocolos compatíveis para ver o percentual."}
                    </p>
                  )}

                  <div className="skinfold-record__section skinfold-record__section--flush">
                    <h3 className="skinfold-record__section-title">
                      Composição corporal
                    </h3>
                    {compositionReference?.percent != null ? (
                      <ul className="skinfold-record__composition-list">
                        <li className="skinfold-record__composition-row">
                          <span>Protocolo de referência</span>
                          <strong>{compositionReference.label}</strong>
                        </li>
                        <li className="skinfold-record__composition-row">
                          <span>% gordura</span>
                          <strong>
                            {formatPercent(compositionReference.percent)}
                          </strong>
                        </li>
                        <li className="skinfold-record__composition-row">
                          <span>Massa gorda</span>
                          <strong>{formatKg(composition.fatMassKg)}</strong>
                        </li>
                        <li className="skinfold-record__composition-row">
                          <span>Massa magra</span>
                          <strong>{formatKg(composition.leanMassKg)}</strong>
                        </li>
                      </ul>
                    ) : (
                      <p className="skinfold-record__bodyfat-empty">
                        {!calculationContext.canCalculateComposition
                          ? "É necessário peso + protocolo marcado para massa gorda e magra."
                          : "Marque um protocolo compatível e selecione a referência para ver a composição."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {(isDraftSession || isEditMode) && (
          <div className="new-circumference-record__warning">
            <p>
              As alterações são salvas automaticamente nesta tela. Use Salvar no hub de
              antropometria para aplicar ao histórico.
            </p>
          </div>
        )}

        {formError && (
          <p className="new-circumference-record__error">{formError}</p>
        )}

        {(isDraftSession || isEditMode) && (
          <div className="new-circumference-record__footer">
            {isDraftSession ? (
              <>
                <DefaultButton
                  title="Descartar e voltar"
                  type="secondary"
                  onClick={() => setShowDiscardModal(true)}
                />
                <DefaultButton
                  title="Salvar"
                  type="primary"
                  onClick={handleSaveAndReturn}
                  disabled={!isDirty}
                />
              </>
            ) : (
              <>
                <DefaultButton
                  title="Voltar"
                  type="secondary"
                  onClick={handleReturnToAnthropometry}
                />
                <DefaultButton
                  title="Salvar"
                  type="primary"
                  onClick={handleSaveAndReturn}
                  disabled={!isDirty}
                />
              </>
            )}
          </div>
        )}
      </div>

      {isDraftSession && (
        <DefaultModal
          title={isDirty ? "Descartar alterações" : "Voltar ao hub"}
          showModal={showDiscardModal}
          onClose={() => setShowDiscardModal(false)}
        >
          <div className="new-circumference-record__confirm">
            <p>
              {isDirty
                ? "As alterações desta tela serão revertidas e você voltará ao hub. O registro será mantido."
                : "Nenhuma alteração foi feita nesta tela. Deseja voltar ao hub?"}
            </p>
            <div className="new-circumference-record__confirm__actions">
              <DefaultButton
                title="Cancelar"
                type="secondary"
                onClick={() => setShowDiscardModal(false)}
              />
              <DefaultButton
                title={isDirty ? "Descartar e voltar" : "Voltar ao hub"}
                type="primary"
                onClick={() => {
                  setShowDiscardModal(false);
                  handleDiscardChanges();
                }}
              />
            </div>
          </div>
        </DefaultModal>
      )}

      <DefaultModal
        title="Valor incomum"
        showModal={showUnusualModal}
        onClose={handleCancelUnusual}
        className="new-circumference-record__confirm-modal"
      >
        <div className="new-circumference-record__confirm">
          <p>Valor incomum. Deseja confirmar?</p>
          <div className="new-circumference-record__confirm__actions">
            <DefaultButton
              title="Corrigir"
              type="secondary"
              onClick={handleCancelUnusual}
            />
            <DefaultButton
              title="Confirmar"
              type="primary"
              onClick={handleConfirmUnusual}
            />
          </div>
        </div>
      </DefaultModal>
    </div>
  );
};

export default SkinfoldRecord;
